import { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, FormControl, InputLabel, Select, MenuItem,
  Grid, Alert, CircularProgress, Typography, Box,
} from '@mui/material';
import type { Study } from '../../api/types';
import { api } from '../../api/rpc';

interface ScheduleOrderDialogProps {
  open: boolean;
  study: Study | null;
  onClose: () => void;
  onComplete: () => void;
}

export const ScheduleOrderDialog = ({ open, study, onClose, onComplete }: ScheduleOrderDialogProps) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scheduleDate, setScheduleDate] = useState('');
  const [ae, setAe] = useState('');
  const [duration, setDuration] = useState(30);

  const handleSchedule = async () => {
    if (!study || !scheduleDate) return;
    setSaving(true);
    setError(null);
    try {
      const dateMs = new Date(scheduleDate).getTime();
      if (isNaN(dateMs)) {
        setError('Invalid date');
        setSaving(false);
        return;
      }
      await api.scheduleOrder(
        study.objKey,
        dateMs,
        ae || undefined,
        duration || undefined,
      );
      onComplete();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to schedule order');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Schedule Order</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {study && (
          <Box sx={{ mb: 2, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="body2">
              <strong>{study.name}</strong> — {study.acc} — {study.mod}
            </Typography>
          </Box>
        )}
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid size={{ xs: 12 }}>
            <TextField
              label="Schedule Date & Time"
              type="datetime-local"
              value={scheduleDate}
              onChange={e => setScheduleDate(e.target.value)}
              fullWidth size="small"
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <TextField
              label="AE Title"
              value={ae}
              onChange={e => setAe(e.target.value)}
              fullWidth size="small"
              placeholder={study?.ae ?? ''}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Duration (min)</InputLabel>
              <Select
                value={duration}
                label="Duration (min)"
                onChange={e => setDuration(Number(e.target.value))}
              >
                {[15, 30, 45, 60, 90, 120].map(d => (
                  <MenuItem key={d} value={d}>{d} min</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          onClick={handleSchedule}
          variant="contained"
          disabled={saving || !scheduleDate}
        >
          {saving ? <CircularProgress size={20} /> : 'Schedule'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
